import type { LanguageType } from '@/types/language';

/**
 * ### SafetyImageAsset
 *
 * One raster under `/images/apps/solution/safety/` with its intrinsic size, so
 * the page can reserve the box before the file arrives.
 */
export type SafetyImageAsset = {
  src: string;
  width: number;
  height: number;
};

/**
 * ### SafetyFeature
 *
 * One of the three capabilities the page leads with. Every string is a
 * dictionary key; the icon is passed through as authored.
 */
export type SafetyFeature = {
  id: string;
  icon: string;
  title: string;
  description: string;
};

/**
 * ### SAFETY_FEATURES
 *
 * The three capabilities in the order the 2026-09 Safety brief lists them:
 * what it hears, where it can go, and that it keeps working in noise. The
 * noise claim is deliberately bounded to busy environments rather than "any"
 * environment; the broader phrasing has no measurement behind it.
 */
export const SAFETY_FEATURES: ReadonlyArray<SafetyFeature> = [
  {
    id: 'rescue-speech',
    icon: '/images/apps/solution/safety/sec_02_img_01.svg',
    title: '구조 요청 언어 감지',
    description:
      '“살려주세요”, “도와주세요” 같은 구조 요청 음성과 비명, 고성 등 응급 상황의 소리를 실시간으로 감지해요.',
  },
  {
    id: 'camera-blind-spots',
    icon: '/images/apps/solution/safety/sec_02_img_04.svg',
    title: '영상 사각지대 보완',
    description:
      '화장실, 탈의실처럼 카메라를 둘 수 없는 공간에서도 소리로 위험 상황을 파악할 수 있어요.',
  },
  {
    id: 'noise-robust',
    icon: '/images/apps/solution/safety/sec_02_img_02.svg',
    title: '소음에 강한 분석 기능',
    description:
      '실제 현장에서 수집한 데이터로 학습해 번잡하고 소음이 많은 환경에서도 위험 신호를 구분해요.',
  },
];

/**
 * ### SafetyEvidenceMetric
 *
 * One published credential with the scope it holds under. `value` is
 * identical in every language.
 */
export type SafetyEvidenceMetric = {
  value: string;
  label: string;
  scope: string;
};

/**
 * ### SAFETY_EVIDENCE_METRICS
 *
 * The three credentials cleared for the Safety page, in the order a buyer
 * weighs them: the certified detection figure, the patents behind it, and
 * the research venue. The figure is the TTA-certified F1-score already shown
 * on the About page; the paper facts come from the Listen AI
 * Safety v2.0 update deck (2026-07-29). Patent wording follows the marketing
 * Slack review (thread 1789956183.734849) and the user's 2026-10-01 update.
 * No other Safety number is published
 * until it has a source of the same standing.
 */
export const SAFETY_EVIDENCE_METRICS: ReadonlyArray<SafetyEvidenceMetric> = [
  {
    value: '98%+',
    label: 'TTA 인증 F1-score',
    scope:
      'TTA(한국정보통신기술협회) 시험 인증에서 확인한 Listen AI Safety의 위험 소리 감지 성능이에요.',
  },
  {
    value: '10건+',
    label: '국내외 특허 등록',
    scope:
      '특허 10건을 등록했으며, 최근 3년간 해외 출원을 포함해 10건 이상을 출원했어요.',
  },
  {
    value: '2024-2026 ICASSP',
    label: '세계 1위 신호처리 학회 논문 게재',
    scope:
      'h-index 기준 세계 1위 신호처리 학회 ICASSP 2024를 비롯해 마이크·환경 차이에 강한 소리 분류 기술 논문을 매년 발표하고 있어요.',
  },
];

/**
 * ### SafetyCompositePoint
 *
 * One of the three sentences that explain how a composite event is decided.
 */
export type SafetyCompositePoint = {
  title: string;
  description: string;
};

/**
 * ### SAFETY_COMPOSITE_EVENT
 *
 * The three points under the replayed composite-event pop-up: how the
 * combination is configured, what a composite event is, and what it
 * removes. The pop-up itself is driven by `safetyCompositeScenario.ts`.
 */
export const SAFETY_COMPOSITE_EVENT = {
  points: [
    {
      title: '현장별 조건 설정',
      description:
        '장소마다 어떤 소리가 몇 초 안에 함께 나야 위험인지 조건을 정해요.',
    },
    {
      title: '신호를 상황으로 묶기',
      description:
        '조건을 만족한 신호들은 개별 알림이 아니라 하나의 위험 상황으로 묶여 보고돼요.',
    },
    {
      title: '확인이 필요한 상황에 집중',
      description:
        '단발성 소리로 생기는 불필요한 알림을 줄여 관리자가 실제 상황에 집중할 수 있어요.',
    },
  ] as ReadonlyArray<SafetyCompositePoint>,
};

/**
 * ### SafetySoundCluster
 *
 * One group of detected sounds, named by the situation it points to rather
 * than by acoustics, with the sounds it holds.
 */
export type SafetySoundCluster = {
  id: string;
  title: string;
  description: string;
  sounds: ReadonlyArray<string>;
  /**
   * Site languages the group's classes are offered in. Omitted, the group
   * ships everywhere. The rescue calls are Korean phrases the model hears
   * ("살려줘", "도와주세요"), so an English reader is not told the product
   * catches the English words.
   */
  languages?: ReadonlyArray<LanguageType>;
};

/**
 * ### SAFETY_SOUND_CLUSTERS
 *
 * The detected sounds, grouped by situation. Korean readers see the
 * fourteen classes of the Korean v2.0 overview deck (2026-03) and English
 * readers the twelve of the English deck (2026-04), which leaves out the
 * two Korean rescue phrases; either way the page cannot name a class the
 * shipping model does not have, which would read as a promise that a new
 * customer gets it on delivery. The speech sounds sit under potential
 * danger — they are cues to a situation that can escalate, such as a
 * fight — and no group describes the product as listening to what is
 * being said.
 */
export const SAFETY_SOUND_CLUSTERS: ReadonlyArray<SafetySoundCluster> = [
  {
    id: 'rescue',
    title: '구조 요청',
    description: '사람이 직접 도움을 요청하는 소리',
    sounds: ['살려주세요', '도와주세요'],
    languages: ['ko'],
  },
  {
    id: 'scream',
    title: '비명·고성',
    description: '위급하거나 공격적인 상황에서 나는 소리',
    sounds: ['여성 비명', '남성 비명', '여성 고성', '남성 고성'],
  },
  {
    id: 'incident',
    title: '사고·이상 상황',
    description: '사고나 위급 상황을 파악하는 데 도움이 되는 소리',
    sounds: ['울음·흐느낌', '헐떡거림', '유리 깨짐', '물리적 충격'],
  },
  {
    id: 'potential',
    title: '잠재 위험 상황',
    description: '싸움처럼 위험으로 번질 수 있는 상황을 감지하는 소리',
    sounds: ['여성 대화', '남성 대화'],
  },
  {
    id: 'surroundings',
    title: '차량·주변 소음',
    description: '현장 주변에서 나는 소리',
    sounds: ['사이렌', '차 경적'],
  },
];

/**
 * ### isSafetySoundOffered
 *
 * Whether a detected sound is offered to readers of a site language, so the
 * sound list and the composite-event demo never disagree about it.
 *
 * @param sound Dictionary key of the sound
 * @param lang Site language the page is rendered in
 * @returns `false` only when the sound's group is limited to other languages
 */
export function isSafetySoundOffered(sound: string, lang: LanguageType) {
  const cluster = SAFETY_SOUND_CLUSTERS.find((item) =>
    item.sounds.includes(sound),
  );

  return !cluster?.languages || cluster.languages.includes(lang);
}

/**
 * ### SAFETY_MONITORING_DASHBOARD
 *
 * The live dashboard capture the monitoring section shows, one per page
 * language, both of the v2.0 console with a detection pop-up over it. Each
 * is a file of Safety's own: the previous Korean capture is shared with the
 * custom solution page, so replacing it in place would have changed that
 * page too.
 */
export const SAFETY_MONITORING_DASHBOARD: Record<
  LanguageType,
  SafetyImageAsset
> = {
  ko: {
    src: '/images/apps/solution/safety/monitoring-dashboard.webp',
    width: 1269,
    height: 637,
  },
  en: {
    src: '/images/apps/solution/safety/monitoring-dashboard-en.webp',
    width: 1357,
    height: 656,
  },
};

/**
 * ### SAFETY_MONITORING_DASHBOARD_ALT
 *
 * Dictionary key of the capture's alternative text.
 */
export const SAFETY_MONITORING_DASHBOARD_ALT = '실시간 분석 대시보드 화면';

/**
 * ### SafetyMonitoringFeature
 *
 * One capability of the monitoring system, described in a sentence or two.
 */
export type SafetyMonitoringFeature = {
  title: string;
  description: string;
};

/**
 * ### SAFETY_MONITORING_FEATURES
 *
 * The five capabilities of the monitoring system, restored on 2026-09-22 to
 * the wording the previous site used, which explains each one more fully
 * than the 2026-09 brief's rewrite did. Analysis and alerting are named
 * together because the pop-up is the alert; the separate alert-channel entry
 * was dropped because those integrations are not offered.
 */
export const SAFETY_MONITORING_FEATURES: ReadonlyArray<SafetyMonitoringFeature> =
  [
    {
      title: 'AI 실시간 분석 및 알림',
      description:
        '직접 설정한 조건의 위험 상황이 발생하면 즉시 팝업 알림을 제공해요. 분석 주기, 민감도, 분석 대상 소리 등 세부 조건을 설정해 목적에 맞게 관리할 수 있어요.',
    },
    {
      title: '위험상황 통계 기능',
      description:
        '요일별, 기간별, 위험 상황 별 통계 정보를 시스템에서 확인할 수 있어요. 수집된 데이터를 바탕으로 유용한 분석 인사이트를 제공해요.',
    },
    {
      title: '개인정보보호 모드',
      description:
        '공공장소 등에 특화된 개인정보보호 모드를 제공해요. 개인정보와 관련된 민감한 데이터는 분석에서 제외하고, 분석 후에 바로 데이터를 파기하기 때문에 보안사고에도 안전해요.',
    },
    {
      title: '간편한 이벤트 검색과 관리',
      description:
        '사운드 이벤트를 들어볼 필요 없이 필터링과 검색만으로 빠르게 찾아볼 수 있어요. 각 위험 상황 소리 이벤트의 상세 정보와 즐겨찾기 편의 기능으로 히스토리 관리가 용이해요.',
    },
    {
      title: '모니터링 장비 관리',
      description:
        '현장 모니터링 장비 연결 및 작동상태를 시스템 내에서도 확인하고 관리할 수 있어요. 상세 분석 설정, 재시작 조작을 원격으로 설정할 수 있어요.',
    },
  ];

/**
 * ### SafetyCasePhoto
 *
 * One installation photo with the place it shows.
 */
export type SafetyCasePhoto = SafetyImageAsset & { caption: string };

/**
 * ### SafetyCase
 *
 * One published Safety deployment: who, where the microphones went, and what
 * that achieved. `customer` is published only within what the customer has
 * cleared; `logo` is a file under `/images/apps/home/`.
 */
export type SafetyCase = {
  id: string;
  customer: string;
  logo: string;
  title: string;
  points: ReadonlyArray<string>;
  photos: ReadonlyArray<SafetyCasePhoto>;
};

/**
 * ### SAFETY_CASES
 *
 * The deployments shown while the case board has nothing published for
 * Safety, in the order the 2026-09 brief set. Every photo is an original the
 * customer supplied, so a card names only the places there is a usable
 * photograph of rather than the brief's full list of them. The first photo
 * establishes the site and the rest show where the microphones went.
 */
export const SAFETY_CASES: ReadonlyArray<SafetyCase> = [
  {
    id: 'government-gym',
    customer: '정부청사관리본부',
    logo: '/images/apps/home/logo_sejong-transparent.png',
    title: '체육관 위험 상황 감지',
    points: [
      'CCTV 설치가 어려운 화장실과 탈의실 위주로 설치',
      '어린이와 고령층 이용이 많은 공간의 응급 상황 모니터링',
    ],
    photos: [
      {
        src: '/images/apps/solution/safety/case-gym-entrance.webp',
        width: 750,
        height: 1000,
        caption: '체육관 출입구',
      },
      {
        src: '/images/apps/solution/safety/case-gym-restroom.webp',
        width: 750,
        height: 1000,
        caption: '화장실',
      },
    ],
  },
  {
    id: 'incheon-transit-station',
    customer: '인천교통공사',
    logo: '/images/apps/home/logo_incheon_transit-transparent.png',
    title: '역사 화장실 위험 상황 감지',
    points: [
      'CCTV가 닿지 않는 개찰구 밖 역사 화장실에 설치',
      '승객이 몰리는 시간대의 구조 요청과 위험 상황을 실시간 감지',
    ],
    photos: [
      {
        src: '/images/apps/solution/safety/case-station-entrance.webp',
        width: 1000,
        height: 750,
        caption: '화장실 입구',
      },
      {
        src: '/images/apps/solution/safety/case-station-restroom.webp',
        width: 1000,
        height: 750,
        caption: '남자 화장실',
      },
      {
        src: '/images/apps/solution/safety/case-station-stalls.webp',
        width: 1000,
        height: 750,
        caption: '화장실 칸막이',
      },
    ],
  },
  {
    id: 'naejangsan-park',
    customer: '내장산국립공원사무소',
    logo: '/images/apps/home/logo_naejangsan-transparent.png',
    title: '국립공원 안전 모니터링',
    points: [
      '케이블카 상부, 산책로, 공중화장실 등 실내외 위험 상황 모니터링',
      '감지된 신호를 현장 대응으로 즉시 연결',
    ],
    photos: [
      {
        src: '/images/apps/solution/safety/case-park-restroom.webp',
        width: 1000,
        height: 750,
        caption: '공중화장실',
      },
      {
        src: '/images/apps/solution/safety/case-park-control.webp',
        width: 1000,
        height: 564,
        caption: '관제실',
      },
    ],
  },
];

/**
 * ### SAFETY_PROCUREMENT_URL
 *
 * Listen AI Safety's entry on the Public Procurement Service's innovation
 * marketplace. Session-style deep link confirmed reachable on 2026-09-20.
 */
export const SAFETY_PROCUREMENT_URL =
  'https://ppi.g2b.go.kr:8914/sm/dm/sch/searchGoodsDetail.do?invGdsIdntNo=00037623';

/**
 * ### SAFETY_INNOVATION_PRODUCT
 *
 * The innovative-product designation as its own section: the official mark,
 * one sentence, and the institutions running pilot purchases. The count of
 * institutions is true as of a date, so the date lives inside the same
 * string and cannot be separated from the figure. Each institution is
 * written with the level of government above it, as the procurement listing
 * writes them, so the five read as one list rather than five conventions.
 */
export const SAFETY_INNOVATION_PRODUCT = {
  eyebrow: '조달청 혁신제품 지정',
  statement:
    '디플리는 혁신제품으로 지정되어 5개 공공기관과 시범구매 사업을 진행하고 있습니다 (2026년 9월 기준).',
  mark: {
    src: '/images/apps/solution/safety/innovative-product-mark.webp',
    width: 604,
    height: 604,
  } as SafetyImageAsset,
  markAlt: '조달청 혁신제품 지정 마크',
  institutionsLabel: '시범구매 진행 기관',
  institutions: [
    '대구광역시 서구',
    '경상북도 성주군',
    '서울시 양천구시설관리공단',
    '서울특별시 도봉구',
    '인천교통공사',
  ] as ReadonlyArray<string>,
  linkLabel: '조달청 혁신제품 바로가기',
  href: SAFETY_PROCUREMENT_URL,
};
